import React, { useState, useRef, useEffect, useCallback } from 'react';
import './FloorplanViewer.css';
import { SENSOR_TYPES, SENSOR_COLORS, DRAWING_MODES, SPACE_TYPES, SPACE_CATEGORY_VISIBILITY, SPACE_TYPE_COLORS } from '../utils/constants';
import DeleteSpaceButton from './DeleteSpaceButton';
import { useSpaceManagement } from '../hooks/useSpaceManagement';
import ShapeDrawingTool from './ShapeDrawingTool';
import ScaleTool from './ScaleTool';
import SpaceTable from './SpaceTable';
import useZoom from '../hooks/useZoom';
import DeleteSelectedSpacesButton from './DeleteSelectedSpacesButton';
import { useAutoSave } from '../hooks/useAutoSave';
import { useAuth } from '../contexts/AuthContext';
import QuoteButton from './quote/QuoteButton';
import ToolPanel from './ToolPanel';
import { calculateOASensors } from '../utils/sensorCalculations';
import SpaceList from './FloorplanViewerModular/SpaceList';
import SpaceLegend from './SpaceLegend';
import QuotePreview from './quote/QuotePreview';
import MeasurementOverlay from './FloorplanViewerModular/MeasurementOverlay';
import { formatArea } from '../utils/conversion';
import ConcurrentUsersIndicator from './ConcurrentUsersIndicator';
import { 
  getBoxCoordinates, 
  calculatePolygonArea, 
  isPointInPolygon,
  snapTo90Degrees
} from '../utils/geometry';

// Constants
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

/**
 * Capitalize the first letter of a string
 * @param {string} string - The string to capitalize
 * @returns {string} - The capitalized string
 */
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

/**
 * FloorplanViewer component for viewing and editing floorplans
 * 
 * @param {Object} props - Component props
 * @param {string} props.imageUrl - URL of the floorplan image
 * @param {Object} props.analysisResults - Results of floorplan analysis (if any)
 * @param {Object} props.initialData - Initial project data (if any)
 * @param {Function} props.onSave - Function to save the project
 * @param {Function} props.onShowSummary - Function to show the summary page
 * @returns {JSX.Element} FloorplanViewer component
 */
const FloorplanViewer = ({ imageUrl, analysisResults, initialData, onSave, onShowSummary }) => {
  // Use the enhanced zoom hook that includes position management
  const { zoom, position, zoomIn, zoomOut, resetZoom, setPosition } = useZoom(1.0, 0.5, 2.0, 0.2);
  // Remove the separate position state since it's now managed by the useZoom hook
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Replace multiple boolean states with a single activeMode state
  const [activeMode, setActiveMode] = useState(DRAWING_MODES.SELECT_MODE); // Default to select mode
  
  // Add state for keyboard shortcut notification
  const [shortcutNotification, setShortcutNotification] = useState({ visible: false, key: '', name: '' });
  
  // Add ceiling height state
  const [ceilingHeight, setCeilingHeight] = useState(initialData?.ceiling_height || 9.0); // Default to 9ft
  
  // Add sensor coverage state
  const [sensorCoverage, setSensorCoverage] = useState(initialData?.sensor_coverage || 200); // Default to 200 sq ft per sensor
  
  // Keep these drawing-related states
  const [measurePoints, setMeasurePoints] = useState([]);
  const [measureDistance, setMeasureDistance] = useState(null);
  const [pixelsPerFoot, setPixelsPerFoot] = useState(initialData?.scale?.pixelsPerFoot || 30); // Default scale: 30px = 1ft
  const [inchesValue, setInchesValue] = useState('');
  const [referenceLine, setReferenceLine] = useState(initialData?.scale?.referenceLine || null);
  const [boxStart, setBoxStart] = useState(null);
  const [boxEnd, setBoxEnd] = useState(null);
  const [boxArea, setBoxArea] = useState(null);
  const [shapePoints, setShapePoints] = useState([]);
  const [isClosingShape, setIsClosingShape] = useState(false);
  
  // Get authentication state
  const { user } = useAuth();
  
  // Conversion factors
  const METERS_TO_FEET = 3.28084;
  const FEET_TO_METERS = 0.3048;
  
  // Use the space management hook
  const {
    boxes,
    shapes,
    entryPoints,
    selectedSpace,
    selectedSpaces,
    isMultiSelecting,
    typeVisibility,
    nextId,
    setBoxes,
    setShapes,
    setEntryPoints,
    setSelectedSpace,
    setSelectedSpaces,
    setIsMultiSelecting,
    deleteSpace,
    deleteSelectedSpaces,
    clearAllSpaces,
    toggleSpaceType,
    updateSpaceCount,
    toggleSpaceVisibility,
    toggleTypeVisibility,
    toggleSelectedSpacesVisibility,
    handleCommandClick,
    handleSingleClick,
    calculateTotalArea,
    calculateSensorCounts,
    formatArea,
    addBox,
    addShape,
    addEntryPoint,
    handleUndo,
    recalculateOASensors,
    updateSpaceAttribute,
    recalculateBoxDimensions,
    history // Also get the history object
  } = useSpaceManagement(initialData);
  
  // State to track space type visibility
  const [spaceTypeVisibility, setSpaceTypeVisibility] = useState(SPACE_CATEGORY_VISIBILITY);
  
  // Add currentSpaceType state
  const [currentSpaceType, setCurrentSpaceType] = useState(SPACE_TYPES.ROOM);
  
  // Basic state tracking - TODO: Move to reducer
  const [modified, setModified] = useState(false);
  const [showSensors, setShowSensors] = useState(false);
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  
  // Show/hide space legend
  const [showSpaceLegend, setShowSpaceLegend] = useState(true);
  
  // Toggle for coloring spaces by sensor type instead of space type
  const [colorBySensorType, setColorBySensorType] = useState(false);
  
  // Keep refs for important elements
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  
  // Add state to track if shift key is pressed
  const [shiftKeyPressed, setShiftKeyPressed] = useState(false);
  const [currentMousePos, setCurrentMousePos] = useState(null);
  
  // Add container dimensions state for ResizeObserver
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  
  // Add ResizeObserver to track container size changes
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Store initial dimensions for comparison
    let initialDimensions = {
      width: containerRef.current.clientWidth, 
      height: containerRef.current.clientHeight
    };
    
    setContainerDimensions(initialDimensions);
    
    // Store the last dimensions to avoid unnecessary recalculations
    let lastDimensions = {...initialDimensions};
    
    const resizeObserver = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      
      // Skip if this is the first measurement or dimensions are 0
      if (width === 0 || height === 0 || !lastDimensions.width || !lastDimensions.height) {
        return;
      }
      
      // Calculate new dimensions
      const newDimensions = { width, height };
      
      // Calculate percent change to determine if update is needed
      const widthChange = Math.abs(newDimensions.width - lastDimensions.width) / lastDimensions.width;
      const heightChange = Math.abs(newDimensions.height - lastDimensions.height) / lastDimensions.height;
      
      // Only update if change is significant (>1%)
      if (widthChange > 0.01 || heightChange > 0.01) {
        console.log('Significant container resize:', 
          lastDimensions.width, 'x', lastDimensions.height, '→', 
          width, 'x', height, 
          `(${(widthChange * 100).toFixed(1)}%, ${(heightChange * 100).toFixed(1)}%)`
        );
        
        // Update container dimensions state for UI
        setContainerDimensions(newDimensions);
        
        // Only recalculate if we have boxes or shapes
        if (boxes.length > 0 || shapes.length > 0 || entryPoints.length > 0) {
          // Use our function to recalculate dimensions
          recalculateBoxDimensions(pixelsPerFoot, lastDimensions, newDimensions);
          
          // Update lastDimensions for next resize
          lastDimensions = {...newDimensions};
        }
      }
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [pixelsPerFoot, recalculateBoxDimensions, boxes, shapes, entryPoints]);
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // We consider the project modified if:
    // 1. Any changes have been made to spaces (boxes, shapes, etc.)
    // 2. Any tool usage that affects the project state
    return modified;
  }, [modified]);
  
  // Initialize auto-save hook with simplified interface
  const { saveStatus, lastSaved, debugMessage } = useAutoSave(
    // Only use autosave if we have an initialData.id (existing project)
    initialData?.id ? true : false, 
    initialData?.id,
    () => {
      // Save function that will be called by the auto-save hook
      if (hasUnsavedChanges()) {
        console.log('Auto-saving project...');
        onSave(getProjectData());
        setModified(false);
      }
    }
  );
  
  /**
   * Creates a darker version of a given color for borders
   * @param {string} color - Hex color code
   * @returns {string} Darker version of the color
   */
  const getDarkerColor = (color) => {
    // If color is not a hex code, return a default dark color
    if (!color || !color.startsWith('#')) return '#333333';
    
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Make each component darker by multiplying by 0.7
    const darkerR = Math.floor(r * 0.7);
    const darkerG = Math.floor(g * 0.7);
    const darkerB = Math.floor(b * 0.7);
    
    // Convert back to hex
    return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
  };
  
  /**
   * Toggle space type visibility
   * @param {string} spaceType - Space type to toggle
   */
  const toggleSpaceTypeVisibility = useCallback((spaceType) => {
    setSpaceTypeVisibility(prev => ({
      ...prev,
      [spaceType]: !prev[spaceType]
    }));
  }, []);
  
  /**
   * Check if a space should be visible based on its type
   * @param {Object} space - Space object with spaceCategory property
   * @returns {boolean} Whether the space should be visible
   */
  const isSpaceTypeVisible = useCallback((space) => {
    if (!space || !space.spaceCategory) return true;
    return spaceTypeVisibility[space.spaceCategory];
  }, [spaceTypeVisibility]);
  
  /**
   * Reset all active tool states and cancel any in-progress operations
   * Used when ESC key is pressed or when switching between tools
   */
  const cancelActiveOperation = useCallback(() => {
    // Clear temporary states
    setMeasurePoints([]);
    setMeasureDistance(null);
    setBoxStart(null);
    setBoxEnd(null);
    setBoxArea(null);
    setShapePoints([]);
    setIsClosingShape(false);
    setInchesValue('');
    
    // Provide visual feedback (could add a toast notification here in the future)
  }, [setMeasurePoints, setMeasureDistance, setBoxStart, setBoxEnd, setBoxArea, setShapePoints, setIsClosingShape, setInchesValue]);
  
  /**
   * Set active tool/mode
   * @param {string} mode - The drawing mode to set from DRAWING_MODES
   */
  const setToolMode = useCallback((mode) => {
    // Cancel any active operation first
    cancelActiveOperation();
    
    // Set the new active mode
    setActiveMode(mode);
  }, [cancelActiveOperation, setActiveMode]);
  
  // Load initial data if available
  useEffect(() => {
    if (initialData) {
      console.log('ENTRY DEBUG - Loading initialData:', initialData);
      console.log('ENTRY DEBUG - Initial entryPoints:', initialData.entryPoints);
      console.log('ENTRY DEBUG - Initial entry_points:', initialData.entry_points);
      
      if (initialData.boxes) setBoxes(initialData.boxes);
      if (initialData.shapes) setShapes(initialData.shapes);
      
      // Handle both naming conventions for backward compatibility
      if (initialData.entryPoints || initialData.entry_points) {
        const entries = initialData.entryPoints || initialData.entry_points || [];
        console.log('ENTRY DEBUG - Setting entryPoints to:', entries);
        setEntryPoints(entries);
      }
      
      if (initialData.scale?.pixelsPerFoot) setPixelsPerFoot(initialData.scale.pixelsPerFoot);
      if (initialData.scale?.referenceLine) setReferenceLine(initialData.scale.referenceLine);
      if (initialData.ceiling_height) setCeilingHeight(initialData.ceiling_height);
      if (initialData.sensor_coverage) setSensorCoverage(initialData.sensor_coverage);
    }
  }, [initialData]);
  
  // Function to recalculate all areas when scale changes
  const recalculateAllAreas = useCallback((oldPixelsPerFoot, newPixelsPerFoot) => {
    if (oldPixelsPerFoot === newPixelsPerFoot) return;
    
    // Calculate the scale ratio
    const scaleRatio = (oldPixelsPerFoot * oldPixelsPerFoot) / (newPixelsPerFoot * newPixelsPerFoot);
    
    console.log('Recalculating areas with scale ratio:', scaleRatio);
    
    // Update boxes
    setBoxes(prevBoxes => prevBoxes.map(box => {
      // Convert area based on new scale
      const newArea = box.area * scaleRatio;
      
      // If it's an OA sensor, also recalculate the recommended count
      let newCount = box.count;
      if (box.type === SENSOR_TYPES.OA) {
        // Use literal value instead of the METERS_TO_FEET constant
        newCount = calculateOASensors(newArea, sensorCoverage, 3.28084, ceilingHeight);
      }
      
      return {
        ...box,
        area: newArea,
        count: newCount
      };
    }));
    
    // Update shapes
    setShapes(prevShapes => prevShapes.map(shape => {
      // Convert area based on new scale
      const newArea = shape.area * scaleRatio;
      
      // If it's an OA sensor, also recalculate the recommended count
      let newCount = shape.count;
      if (shape.type === SENSOR_TYPES.OA) {
        // Use literal value instead of the METERS_TO_FEET constant
        newCount = calculateOASensors(newArea, sensorCoverage, 3.28084, ceilingHeight);
      }
      
      return {
        ...shape,
        area: newArea,
        count: newCount
      };
    }));
  }, [setBoxes, setShapes, ceilingHeight, sensorCoverage, calculateOASensors]);
  
  // Monitor changes to pixelsPerFoot and update areas if needed
  const previousPixelsPerFootRef = useRef(pixelsPerFoot);
  useEffect(() => {
    // Skip the initial render
    if (previousPixelsPerFootRef.current === pixelsPerFoot) {
      return;
    }
    
    // Store old value for comparison
    const oldPixelsPerFoot = previousPixelsPerFootRef.current;
    
    // Update reference for next change
    previousPixelsPerFootRef.current = pixelsPerFoot;
    
    // Skip recalculation during initial load
    if (initialData && initialData.scale?.pixelsPerFoot === pixelsPerFoot) {
      return;
    }
    
    // If we get here, the pixelsPerFoot was changed outside the normal flow
    // and we should recalculate all areas
    console.log('Scale changed from', oldPixelsPerFoot, 'to', pixelsPerFoot);
    recalculateAllAreas(oldPixelsPerFoot, pixelsPerFoot);
    
  }, [pixelsPerFoot, initialData, recalculateAllAreas]);
  
  // Prepare project data for saving
  const getProjectData = () => {
    console.log('ENTRY DEBUG - Preparing project data for saving');
    console.log('ENTRY DEBUG - Current entryPoints state:', entryPoints);
    
    const projectData = {
      boxes,
      shapes,
      entryPoints,
      scale: {
        pixelsPerFoot,
        referenceLine
      },
      ceilingHeight,
      sensor_coverage: sensorCoverage
    };
    
    console.log('ENTRY DEBUG - Returning project data with entryPoints:', projectData.entryPoints);
    return projectData;
  };
  
  // Display debug message in a floating notification if it exists
  useEffect(() => {
    // Don't show debug messages in the UI
    if (debugMessage) {
      // Log to console only for debugging
      console.log("[Debug]", debugMessage);
      
      // Remove any existing toast element
      const existingToast = document.getElementById('debug-save-toast');
      if (existingToast) {
        existingToast.style.display = 'none';
      }
    }
  }, [debugMessage]);
  
  // Manual save function
  const saveProject = async () => {
    if (onSave) {
      // Add temporary clicked state to the save button
      const saveBtn = document.querySelector('.save-button');
      if (saveBtn) {
        saveBtn.classList.add('clicked');
        
        // Remove the clicked state after a short delay
        setTimeout(() => {
          saveBtn.classList.remove('clicked');
        }, 300);
      }
      
      const data = getProjectData();
      console.log('ENTRY DEBUG - Saving project with data:', data);
      console.log('ENTRY DEBUG - Entry points in save data:', data.entryPoints);
      
      return await onSave(data);
    }
    return null;
  };

  // Add save keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveProject();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [boxes, shapes, entryPoints, pixelsPerFoot, referenceLine, ceilingHeight]);
  
  // Format measurement based on current unit
  const formatMeasurement = (meters) => {
    // Convert to inches
    const totalInches = meters * METERS_TO_FEET * 12;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    
    if (inches === 0) {
      return `${feet}'`;
    } else if (inches === 12) {
      return `${feet + 1}'`;
    } else {
      return `${feet}' ${inches}"`;
    }
  };
  
  // Create a unified transform style that applies to both the image and overlay
  const transformStyle = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`
  };
  
  // Get cursor position relative to the image with proper zoom consideration
  const getCursorPosition = (event) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    
    const rect = containerRef.current.getBoundingClientRect();
    
    // Calculate cursor position relative to the container (in viewport coordinates)
    const viewportX = event.clientX - rect.left;
    const viewportY = event.clientY - rect.top;
    
    // Simple, direct calculation: subtract position offset and divide by zoom
    const x = (viewportX - position.x) / zoom;
    const y = (viewportY - position.y) / zoom;
    
    // DEBUG: Log the coordinates for comparison
    console.log('DEBUG Coordinates:', {
      redBoxOrigin: { x: 0, y: 0 },
      cursorViewport: { x: viewportX, y: viewportY },
      cursorTransformed: { x, y },
      containerRect: rect,
      transform: { position, zoom }
    });
    
    return { x, y };
  };
  
  // Handle keyboard shortcuts for zooming
  useEffect(() => {
    const handleKeyDown = (e) => {
      /*
      // ZOOM FUNCTIONALITY DISABLED FOR NOW
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        if (containerRef.current) {
          const viewportSize = {
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight
          };
          zoomIn(viewportSize);
        }
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        if (containerRef.current) {
          const viewportSize = {
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight
          };
          zoomOut(viewportSize);
        }
      } else if (e.key === '0') {
        e.preventDefault();
        if (containerRef.current) {
          const viewportSize = {
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight
          };
          resetZoom(viewportSize);
        }
      }
      */
      
      // Check for ESC key (key code 27)
      if (e.key === 'Escape' || e.keyCode === 27) {
        cancelActiveOperation();
        
        // Reset to selection mode when ESC is pressed
        setActiveMode(DRAWING_MODES.SELECT_MODE);
        
        // If in selection mode and a space is selected, deselect it
        if (activeMode === DRAWING_MODES.SELECT_MODE && (selectedSpace || selectedSpaces.length > 0)) {
          setSelectedSpace(null);
          setSelectedSpaces([]);
          setIsMultiSelecting(false);
          console.log('ESC key pressed: Cleared space selection');
        }
      }
      
      // Check for Delete key to delete selected spaces
      if (e.key === 'Delete' || e.key === 'Backspace') {
        console.log('Delete key pressed');
        console.log('Active mode:', activeMode);
        console.log('Selected spaces:', selectedSpaces);
        console.log('Selected space:', selectedSpace);
        
        // Only allow deletion in selection mode
        if (activeMode === DRAWING_MODES.SELECT_MODE) {
          console.log('In SELECT_MODE, attempting deletion');
          if (selectedSpaces && selectedSpaces.length > 0) {
            console.log(`Deleting ${selectedSpaces.length} selected spaces`);
            deleteSelectedSpaces();
          } else if (selectedSpace) {
            console.log(`Deleting single space: ${selectedSpace.type} with ID ${selectedSpace.id}`);
            // Use the correct function call format for single space deletion
            if (typeof deleteSpace === 'function') {
              deleteSpace(selectedSpace.type, selectedSpace.id, pixelsPerFoot);
            } else {
              console.error('deleteSpace is not a function!', deleteSpace);
            }
          } else {
            console.log('Nothing selected to delete');
          }
        } else {
          console.log(`Not in SELECT_MODE, deletion not allowed`);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoom, position, zoomIn, zoomOut, resetZoom, activeMode, selectedSpaces, selectedSpace, deleteSelectedSpaces, deleteSpace, pixelsPerFoot, cancelActiveOperation, setIsMultiSelecting]);
  
  // Handle wheel event for zooming (if you want to re-enable it)
  const handleWheel = (event) => {
    // Mouse wheel zoom is disabled in favor of keyboard shortcuts (+ and - keys)
    // But we could enable it if needed
    return;
  };
  
  // Handle mouse down for dragging, measuring preview, or drawing box
  const handleMouseDown = (event) => {
    // Check if the event is coming from the tool panel or another UI element that should be ignored
    if (event.target.closest('.tool-panel')) {
      // Don't process mouse events that originated from the tool panel
      return;
    }
    
    const cursorPos = getCursorPosition(event);
    // DEBUG logging
    // console.log(`[MOUSE DOWN] at position:`, cursorPos);
    // console.log(`Active mode: ${activeMode}`);
    
    // Log the position relative to the image container (the red box)
    const imageContainerRect = imageRef.current?.getBoundingClientRect();
    if (imageContainerRect) {
      const relativeX = (event.clientX - imageContainerRect.left) / zoom;
      const relativeY = (event.clientY - imageContainerRect.top) / zoom;
      // console.log('Position relative to red box canvas:', { x: relativeX, y: relativeY });
    }

    // DEBUG: Draw a marker for where boxes are created
    /*
    if (activeMode === DRAWING_MODES.DRAWING_BOX && !boxStart) {
      // Create a temporary marker for where the box will start
      const markerId = 'box-start-marker';
      let marker = document.getElementById(markerId);
      
      if (!marker) {
        const svgMarkers = document.getElementById('debug-markers') || (() => {
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('id', 'debug-markers');
          svg.setAttribute('style', 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999;');
          containerRef.current.appendChild(svg);
          return svg;
        })();
        
        marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        marker.setAttribute('id', markerId);
        marker.setAttribute('fill', 'green');
        marker.setAttribute('stroke', 'white');
        marker.setAttribute('stroke-width', '2');
        svgMarkers.appendChild(marker);
        
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('id', `${markerId}-label`);
        label.setAttribute('fill', 'green');
        label.setAttribute('stroke', 'white');
        label.setAttribute('stroke-width', '0.5');
        label.setAttribute('font-size', '14px');
        svgMarkers.appendChild(label);
      }
      
      // Position in screen coordinates
      const screenX = cursorPos.x * zoom + position.x;
      const screenY = cursorPos.y * zoom + position.y;
      
      marker.setAttribute('cx', screenX);
      marker.setAttribute('cy', screenY);
      marker.setAttribute('r', '8');
      
      const label = document.getElementById(`${markerId}-label`);
      if (label) {
        label.setAttribute('x', screenX + 15);
        label.setAttribute('y', screenY + 5);
        label.textContent = `Box Start (${Math.round(cursorPos.x)},${Math.round(cursorPos.y)})`;
      }
    }
    */

    // First, handle setting scale
    if (activeMode === DRAWING_MODES.SETTING_SCALE) {
      if (measurePoints.length === 0) {
        console.log('Setting first measurement point');
        setMeasurePoints([cursorPos]);
      } else if (measurePoints.length === 1) {
        console.log('Setting second measurement point');
        const newMeasurePoints = [...measurePoints, cursorPos];
        setMeasurePoints(newMeasurePoints);
        
        // Calculate the distance in pixels between the two points
        const dx = newMeasurePoints[1].x - newMeasurePoints[0].x;
        const dy = newMeasurePoints[1].y - newMeasurePoints[0].y;
        const pixelDistance = Math.sqrt(dx * dx + dy * dy);
        
        // Store this as the reference
        setMeasureDistance(pixelDistance);
        
        console.log(`Pixel distance: ${pixelDistance}`);
        
        // Create the reference line object to be displayed
        setReferenceLine({
          points: newMeasurePoints,
          pixelDistance
        });
      }
      return;
    }

    // Handle measuring tool
    if (activeMode === DRAWING_MODES.MEASURING) {
      if (measurePoints.length === 0) {
        console.log('Setting first measurement point for measuring');
        setMeasurePoints([cursorPos]);
      } else if (measurePoints.length === 1) {
        console.log('Setting second measurement point for measuring');
        const newMeasurePoints = [...measurePoints, cursorPos];
        setMeasurePoints(newMeasurePoints);
        
        // Calculate the distance in pixels between the two points
        const dx = newMeasurePoints[1].x - newMeasurePoints[0].x;
        const dy = newMeasurePoints[1].y - newMeasurePoints[0].y;
        const pixelDistance = Math.sqrt(dx * dx + dy * dy);
        
        // Convert to meters using the pixelsPerFoot scale
        const distanceInMeters = pixelDistance / pixelsPerFoot;
        
        // Store this as the measurement
        setMeasureDistance(distanceInMeters);
        
        console.log(`Distance: ${distanceInMeters} meters`);
      } else {
        // Reset if we already have two points
        console.log('Resetting measurement points');
        setMeasurePoints([cursorPos]);
        setMeasureDistance(null);
      }
      return;
    }

    // Handle drawing modes
    if (activeMode === DRAWING_MODES.DRAWING_BOX || activeMode === DRAWING_MODES.DRAWING_SHAPE) {
      // If clicking on existing space while drawing, do nothing
      if (isClickingExistingSpace(cursorPos)) {
        return;
      }

      if (activeMode === DRAWING_MODES.DRAWING_BOX) {
        if (!boxStart) {
          setBoxStart(cursorPos);
          console.log('Box start set to:', cursorPos);
        } else {
          setBoxEnd(cursorPos);
          console.log('Box end set to:', cursorPos);
        }
      } else if (activeMode === DRAWING_MODES.DRAWING_SHAPE) {
        // Get cursor position
        const cursorPos = getCursorPosition(event);
        
        // Check if we're closing the shape
        if (shapePoints.length >= 3 && isPointClose(cursorPos, shapePoints[0])) {
          // Calculate area
          const area = calculatePolygonArea(shapePoints);
          
          // Add shape with the points
          const newShape = {
            points: [...shapePoints],
            area,
            type: SENSOR_TYPES.WAFFLE,
            count: undefined,
            visible: true,
            spaceCategory: currentSpaceType  // Use current space type instead of hardcoding to DESK
          };
          
          const addedShape = addShape(newShape);
          
          // Reset shape drawing state
          setShapePoints([]);
          setIsClosingShape(false);
        } else {
          // Add a new point to the shape
          // If shift is pressed and we already have at least one point,
          // snap the new point to a 90-degree angle from the last point
          let newPoint = cursorPos;
          
          if ((event.shiftKey || shiftKeyPressed) && shapePoints.length > 0) {
            const lastPoint = shapePoints[shapePoints.length - 1];
            newPoint = snapTo90Degrees(lastPoint, cursorPos);
          }
          
          setShapePoints([...shapePoints, newPoint]);
        }
      }
      return;
    }

    // Handle entry point addition
    if (activeMode === DRAWING_MODES.ADDING_ENTRY) {
      console.log('ENTRY DEBUG - Adding an entry point at cursor position:', cursorPos);
      
      const newEntry = {
        position: cursorPos,
        type: SENSOR_TYPES.ENTRY,
        count: 1,
        visible: true,
        spaceCategory: SPACE_TYPES.ENTRY
      };
      
      console.log('ENTRY DEBUG - Creating new entry point:', newEntry);
      
      const addedEntry = addEntryPoint(newEntry);
      console.log('ENTRY DEBUG - Added entry point result:', addedEntry);
      console.log('ENTRY DEBUG - Current entryPoints state after adding:', entryPoints);
      
      // Flag the project as modified to ensure it gets saved
      setModified(true);
      
      return;
    }

    // Handle space selection (only when in selection mode)
    if (activeMode === DRAWING_MODES.SELECT_MODE) {
      console.log(`[SELECTION] Checking for spaces at click position`);
      console.log(`Modifier keys: metaKey=${event.metaKey}, ctrlKey=${event.ctrlKey}`);
      
      let clickedOnSpace = false;
      
      // Convert cursor position to integer coordinates to prevent floating point issues
      const fixedCursorPos = {
        x: Math.round(cursorPos.x),
        y: Math.round(cursorPos.y)
      };
      
      console.log(`Cursor position: x=${fixedCursorPos.x}, y=${fixedCursorPos.y}`);
      
      // Track spaces at click position to handle potential overlaps
      const spacesAtClick = [];
      
      // Check boxes first
      for (const box of boxes) {
        // Create integer-based coordinates for stable comparisons
        const startX = Math.floor(box.start.x);
        const startY = Math.floor(box.start.y);
        const endX = Math.ceil(box.end.x);
        const endY = Math.ceil(box.end.y);
        
        if (
          fixedCursorPos.x >= startX && 
          fixedCursorPos.x <= endX && 
          fixedCursorPos.y >= startY && 
          fixedCursorPos.y <= endY
        ) {
          console.log(`Found box ${box.id} at click position`);
          spacesAtClick.push({ type: 'box', id: box.id });
        }
      }
      
      // Check shapes
      for (const shape of shapes) {
        if (isPointInPolygon(fixedCursorPos, shape.points)) {
          console.log(`Found shape ${shape.id} at click position`);
          spacesAtClick.push({ type: 'shape', id: shape.id });
        }
      }
      
      // If we found spaces at the click position, select one
      if (spacesAtClick.length > 0) {
        clickedOnSpace = true;
        console.log(`Found ${spacesAtClick.length} spaces at click position`);
        
        // Get the most recently added space (highest ID)
        const spaceToSelect = spacesAtClick.reduce((latest, current) => {
          // Compare box IDs or shape IDs based on type
          if (current.type === 'box') {
            const box = boxes.find(b => b.id === current.id);
            if (!latest || box.id > latest.id) return box;
          } else if (current.type === 'shape') {
            const shape = shapes.find(s => s.id === current.id);
            if (!latest || shape.id > latest.id) return shape;
          }
          return latest;
        }, null);
        
        if (spaceToSelect) {
          console.log(`Selecting ${spaceToSelect.id} (highest ID found)`);
          
          if (event.metaKey || event.ctrlKey) {
            console.log(`Command/Ctrl key held, calling handleCommandClick`);
            handleCommandClick(event, spaceToSelect.type, spaceToSelect.id);
          } else {
            console.log(`Normal click, calling handleSingleClick`);
            handleSingleClick(spaceToSelect.type, spaceToSelect.id);
          }
        }
      }
      
      // Check entry points if we didn't click on a box or shape
      if (!clickedOnSpace) {
        const entryClickRadius = 15 / zoom; // Pixel radius for entry point click detection, adjusted for zoom
        
        // Gather all entry points in selection radius
        const entriesAtClick = [];
        
        for (const entry of entryPoints) {
          const dx = entry.position.x - fixedCursorPos.x;
          const dy = entry.position.y - fixedCursorPos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance <= entryClickRadius) {
            entriesAtClick.push(entry);
          }
        }
        
        // If we found entry points, select one (prefer the one with the highest ID)
        if (entriesAtClick.length > 0) {
          console.log(`Found ${entriesAtClick.length} entry points at click position`);
          
          // Sort by ID (descending) and take the first one
          const entryToSelect = entriesAtClick.sort((a, b) => b.id - a.id)[0];
          
          if (event.metaKey || event.ctrlKey) {
            handleCommandClick(event, 'entry', entryToSelect.id);
          } else {
            handleSingleClick('entry', entryToSelect.id);
          }
          clickedOnSpace = true;
        }
      }
      
      if (!clickedOnSpace) {
        console.log(`No space found at click position, clearing selection`);
        // If we clicked on empty space, clear the selection
        setSelectedSpace(null);
        setSelectedSpaces([]);
        setIsMultiSelecting(false);
      }
    }
  };
  
  // Handle mouse move for dragging, measuring preview, or box preview
  const handleMouseMove = (event) => {
    // Check if the event is coming from the tool panel
    if (event.target.closest('.tool-panel')) {
      return;
    }
    
    // Update current mouse position
    const cursorPos = getCursorPosition(event);
    setCurrentMousePos(cursorPos);
    
    if (activeMode === DRAWING_MODES.SETTING_SCALE && measurePoints.length === 1) {
      // Show live measurement preview
      const cursorPos = getCursorPosition(event);
      
      // Check if shift key is pressed for 90-degree snapping
      let snapPos = cursorPos;
      if (event.shiftKey || shiftKeyPressed) {
        snapPos = snapTo90Degrees(measurePoints[0], cursorPos);
      }
      
      const dx = snapPos.x - measurePoints[0].x;
      const dy = snapPos.y - measurePoints[0].y;
      const pixelDistance = Math.sqrt(dx * dx + dy * dy);
      
      // Store raw pixel distance for preview
      setMeasureDistance(pixelDistance);
    } else if (activeMode === DRAWING_MODES.DRAWING_BOX && boxStart) {
      // Show live box preview
      const cursorPos = getCursorPosition(event);
      setBoxEnd(cursorPos);
      
      // Calculate area directly from raw coordinates
      const width = Math.abs(cursorPos.x - boxStart.x);
      const height = Math.abs(cursorPos.y - boxStart.y);
      const pixelArea = width * height;
      const squareMeters = pixelArea / (pixelsPerFoot * pixelsPerFoot);
      setBoxArea(squareMeters);
    } else if (activeMode === DRAWING_MODES.DRAWING_SHAPE && shapePoints.length > 0) {
      // Check if cursor is close to the first point to close the shape
      const cursorPos = getCursorPosition(event);
      
      // Only allow closing if we have at least 3 points
      if (shapePoints.length >= 3) {
        // Check if cursor is close to the first point
        const dx = cursorPos.x - shapePoints[0].x;
        const dy = cursorPos.y - shapePoints[0].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // If cursor is within 10 pixels of the first point, show closing preview
        setIsClosingShape(distance < 10 / zoom);
      } else {
        setIsClosingShape(false);
      }
    }
  };
  
  // Handle mouse up to end dragging
  const handleMouseUp = (event) => {
    // Check if the event is coming from the tool panel
    if (event.target && event.target.closest('.tool-panel')) {
      return;
    }
    
    if (activeMode === DRAWING_MODES.DRAWING_BOX && boxStart && boxEnd) {
      // Calculate area directly from the raw coordinates, not the scaled ones
      const width = Math.abs(boxEnd.x - boxStart.x);
      const height = Math.abs(boxEnd.y - boxStart.y);
      const pixelArea = width * height;
      const areaInSquareMeters = pixelArea / (pixelsPerFoot * pixelsPerFoot);
      
      // Add new box
      const newBox = {
        start: boxStart,
        end: boxEnd,
        area: areaInSquareMeters,
        type: SENSOR_TYPES.WAFFLE,
        count: 1,
        visible: true,
        spaceCategory: currentSpaceType
      };
      
      // Use the hook function to add the box
      const addedBox = addBox(newBox);
      
      // Reset drawing state but keep the tool active
      setBoxStart(null);
      setBoxEnd(null);
      setBoxArea(null);
      
      // Show feedback if box was too small
      if (!addedBox) {
        showTooSmallMessage('Box');
      }
    } else if (activeMode === DRAWING_MODES.DRAWING_SHAPE && isClosingShape && shapePoints.length >= 3) {
      // Calculate area of the polygon
      const areaInSquareMeters = calculatePolygonArea(shapePoints);
      
      // Add new shape
      const newShape = {
        points: [...shapePoints],
        area: areaInSquareMeters,
        type: SENSOR_TYPES.WAFFLE,
        count: 1,
        visible: true,
        spaceCategory: currentSpaceType
      };
      
      // Use the hook function to add the shape
      const addedShape = addShape(newShape);
      
      // Reset drawing state but keep the tool active
      setShapePoints([]);
      setIsClosingShape(false);
      
      // Show feedback if shape was too small
      if (!addedShape) {
        showTooSmallMessage('Shape');
      }
    }
    
    setIsDragging(false);
  };
  
  // Helper function to show "too small" message
  const showTooSmallMessage = (spaceType) => {
    // Display a temporary message
    const messageElement = document.createElement('div');
    messageElement.className = 'too-small-message';
    messageElement.textContent = `${spaceType} too small (minimum 2 ft²)`;
    messageElement.style.position = 'absolute';
    messageElement.style.top = '50%';
    messageElement.style.left = '50%';
    messageElement.style.transform = 'translate(-50%, -50%)';
    messageElement.style.backgroundColor = 'rgba(255, 0, 0, 0.7)';
    messageElement.style.color = 'white';
    messageElement.style.padding = '10px 20px';
    messageElement.style.borderRadius = '5px';
    messageElement.style.zIndex = '1000';
    messageElement.style.fontWeight = 'bold';
    
    document.body.appendChild(messageElement);
    
    // Remove the message after 2 seconds
    setTimeout(() => {
      document.body.removeChild(messageElement);
    }, 2000);
  };
  
  // Simplified tool toggle functions that use setToolMode
  const toggleSelectMode = useCallback(() => setToolMode(DRAWING_MODES.SELECT_MODE), [setToolMode]);
  const toggleDrawBox = useCallback(() => setToolMode(DRAWING_MODES.DRAWING_BOX), [setToolMode]);
  const toggleDrawShape = useCallback(() => setToolMode(DRAWING_MODES.DRAWING_SHAPE), [setToolMode]);
  
  /**
   * Reset view to default (centered, no zoom)
   */
  const resetView = useCallback(() => {
    // Reset position only, zoom is fixed at 1.0
    setPosition({ x: 0, y: 0 });
    console.log('View reset to center');
  }, [setPosition]);
  
  // Handle image load event
  const handleImageLoad = () => {
    // Reset view when image loads
    resetView();
    // You could add additional logic here if needed
    console.log('Floorplan image loaded');
  };
  
  // Handle inches input change
  const handleInchesChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setInchesValue(value);
    }
  };
  
  // Apply the known distance to set the scale
  const applyScale = useCallback((apply) => {
    if (!measureDistance) return;
    
    // Handle cancellation
    if (apply === null) {
      // Cancel the scale setting and revert to the previous state
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      setMeasurePoints([]);
      setMeasureDistance(null);
      setInchesValue('');
      return;
    }
    
    // Get distance in feet directly from input
    if (!inchesValue || isNaN(inchesValue) || parseFloat(inchesValue) <= 0) {
      console.error("Invalid input value:", inchesValue);
      return; // Invalid input
    }
    
    const distanceInFeet = parseFloat(inchesValue);
    
    // Calculate distance between the two points in pixels
    const dx = measurePoints[1].x - measurePoints[0].x;
    const dy = measurePoints[1].y - measurePoints[0].y;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate pixels per foot
    const newPixelsPerFoot = pixelDistance / distanceInFeet;
    
    console.log('Setting scale:', {
      pixelDistance,
      distanceInFeet,
      newPixelsPerFoot,
    });
    
    // Store old pixels per foot for area recalculation
    const oldPixelsPerFoot = pixelsPerFoot;
    
    // Update the scale
    setPixelsPerFoot(newPixelsPerFoot);
    
    // Log after update for debugging
    console.log('Updated pixelsPerFoot:', newPixelsPerFoot);
    
    // Recalculate all areas based on new scale
    recalculateAllAreas(oldPixelsPerFoot, newPixelsPerFoot);
    
    // Save the reference line with distance in feet
    setReferenceLine({
      points: [...measurePoints],
      distance: distanceInFeet, // Store the actual feet value directly
      pixels: pixelDistance
    });
    
    console.log('Saving reference line with distance in feet:', distanceInFeet);
    
    // Reset scale setting mode but keep the points
    setActiveMode(DRAWING_MODES.SELECT_MODE);
    setMeasurePoints([]);
    setMeasureDistance(null);
    setInchesValue('');
  }, [measureDistance, inchesValue, measurePoints, pixelsPerFoot, recalculateAllAreas, setPixelsPerFoot, setReferenceLine, setActiveMode, setMeasurePoints, setMeasureDistance, setInchesValue]);
  
  // Calculate polygon area using Shoelace formula
  const calculatePolygonArea = (points) => {
    if (points.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    
    area = Math.abs(area) / 2;
    // Convert from pixel area to square feet
    return area / (pixelsPerFoot * pixelsPerFoot);
  };
  
  /**
   * Calculate the area of a box in square feet
   * @param {Object} box - Box object with start and end coordinates
   * @returns {number} - Area in square feet
   */
  const calculateBoxArea = (box) => {
    if (!box || !box.start || !box.end) return 0;
    
    const width = Math.abs(box.end.x - box.start.x) / pixelsPerFoot;
    const height = Math.abs(box.end.y - box.start.y) / pixelsPerFoot;
    
    // Area in square feet
    return width * height;
  };
  
  /**
   * Calculate the area of a shape in square feet
   * @param {Object} shape - Shape object with points array
   * @returns {number} - Area in square feet
   */
  const calculateShapeArea = (shape) => {
    if (!shape || !shape.points || shape.points.length < 3) return 0;
    
    // Calculate polygon area directly in square feet
    return calculatePolygonArea(shape.points);
  };
  
  // Check if a point is close to another point
  const isPointClose = (p1, p2, threshold = 10) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold / zoom;
  };
  
  // Check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = (point, polygon) => {
    // Ray casting algorithm for point in polygon detection
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      // Ray casting algorithm
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      
      if (intersect) inside = !inside;
    }
    return inside;
  };
  
  // Check if a point is inside any existing box or shape
  const isClickingExistingSpace = (point) => {
    // Skip check if we're adding an entry point - they can overlap
    if (activeMode === DRAWING_MODES.ADDING_ENTRY) return false;

    // Check boxes first
    for (const box of boxes) {
      if (
        point.x >= box.start.x && 
        point.x <= box.end.x && 
        point.y >= box.start.y && 
        point.y <= box.end.y
      ) {
        return true;
      }
    }

    // Then check shapes
    for (const shape of shapes) {
      if (isPointInPolygon(point, shape.points)) {
        return true;
      }
    }

    return false;
  };
  
  // Format and display the current scale in real-world units
  const getScaleDisplayText = useCallback(() => {
    const pixelsPerFoot = pixelsPerFoot;
    return `Scale: 1' = ${pixelsPerFoot.toFixed(2).toLocaleString()} px`;
  }, [pixelsPerFoot]);
  
  // Format and display the current zoom level
  const getZoomDisplayText = useCallback(() => {
    const zoomPercent = zoom * 100;
    return `Zoom: ${zoomPercent.toFixed(0).toLocaleString()}%`;
  }, [zoom]);
  
  // Export spaces data as JSON
  const exportData = () => {
    const data = {
      boxes: boxes,
      shapes: shapes,
      entryPoints: entryPoints,
      scale: {
        pixelsPerFoot,
        referenceLine
      },
      ceilingHeight: ceilingHeight,
      timestamp: new Date().toISOString()
    };
    
    // Create a blob and download link
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    link.download = `floorplan-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Toggle ceiling height mode
  const toggleCeilingHeight = useCallback(() => setToolMode(DRAWING_MODES.CEILING_HEIGHT), [setToolMode]);
  
  // Handle ceiling height change
  const handleCeilingHeightChange = (e) => {
    const value = e.target.value;
    // Allow empty string or valid decimal number
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Convert to number or use empty string
      const numValue = value === '' ? '' : parseFloat(value);
      setCeilingHeight(numValue);
    }
  };

  // Handle ceiling height blur - ensure we have a valid number
  const handleCeilingHeightBlur = () => {
    // If empty or not a number, reset to default
    if (ceilingHeight === '' || isNaN(ceilingHeight)) {
      setCeilingHeight(9.0);
    } else {
      // Make sure we have a proper number and force a refresh
      const numValue = parseFloat(ceilingHeight);
      console.log(`Ceiling height validated: ${numValue}ft - triggering sensor recalculation`);
      setCeilingHeight(numValue);
    }
  };

  // Add a function to get sensor counts for the quote generator
  const getSensorCountsForQuote = () => {
    const { oaCount, waffleCount, entryCount } = calculateSensorCounts();
    
    // Split waffle count into meeting and desk based on space types
    // This is a simplified approach - you may need to adjust based on your actual data structure
    const waffleMeetingCount = shapes.filter(shape => 
      shape.type === SENSOR_TYPES.WAFFLE && shape.name?.toLowerCase().includes('meeting')
    ).length;
    
    const waffleDeskCount = waffleCount - waffleMeetingCount;
    
    return {
      entry: entryCount,
      oa: oaCount,
      waffleMeeting: waffleMeetingCount,
      waffleDesk: waffleDeskCount
    };
  };

  /**
   * DEBUG: Test functions for coordinate alignment
   */
  /*
  const testCoordinateAlignment = () => {
    // Get the viewer container
    const viewerContainer = containerRef.current;
    if (!viewerContainer) return;
    
    // Create a test marker at a specific point in image space
    const testMarker = document.createElement('div');
    testMarker.id = 'test-coord-marker';
    testMarker.style.position = 'absolute';
    testMarker.style.width = '20px';
    testMarker.style.height = '20px';
    testMarker.style.backgroundColor = 'magenta';
    testMarker.style.borderRadius = '50%';
    testMarker.style.transform = 'translate(-50%, -50%)';
    testMarker.style.pointerEvents = 'none';
    testMarker.style.zIndex = '9999';
    
    // Fixed image coordinates at 100,100 (a point in the image itself)
    const imageX = 100;
    const imageY = 100;
    
    // Convert to screen coordinates
    const testPoint = {
      x: imageX * zoom + position.x,
      y: imageY * zoom + position.y
    };
    
    testMarker.style.left = `${testPoint.x}px`;
    testMarker.style.top = `${testPoint.y}px`;
    
    // Add a label
    const label = document.createElement('div');
    label.textContent = 'Test Point';
    label.style.position = 'absolute';
    label.style.left = '10px';
    label.style.top = '10px';
    label.style.color = 'magenta';
    label.style.fontSize = '12px';
    label.style.fontWeight = 'bold';
    label.style.textShadow = '1px 1px 1px black';
    
    testMarker.appendChild(label);
    
    // Remove any existing marker
    const existingMarker = document.getElementById('test-coord-marker');
    if (existingMarker) {
      existingMarker.remove();
    }
    
    // Add marker to viewer container
    viewerContainer.appendChild(testMarker);
  };
  */
  
  // Call the test when zoom or position changes
  useEffect(() => {
    // DEBUG: Coordinate alignment test
    // testCoordinateAlignment();
    
    // DEBUG: Origin markers
    /*
    // Also update debug markers for origins
    const drawDebugMarkers = () => {
      // Create SVG elements if they don't exist
      if (!document.getElementById('debug-markers')) {
        const svgMarker = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgMarker.setAttribute('id', 'debug-markers');
        svgMarker.setAttribute('style', 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999;');
        
        // Red box origin (0,0)
        const redOrigin = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        redOrigin.setAttribute('id', 'red-origin-marker');
        redOrigin.setAttribute('cx', position.x);
        redOrigin.setAttribute('cy', position.y);
        redOrigin.setAttribute('r', '10');
        redOrigin.setAttribute('fill', 'yellow');
        redOrigin.setAttribute('stroke', 'black');
        redOrigin.setAttribute('stroke-width', '2');
        
        // Add label
        const redLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        redLabel.setAttribute('id', 'red-origin-marker-label');
        redLabel.setAttribute('x', position.x + 15);
        redLabel.setAttribute('y', position.y + 5);
        redLabel.setAttribute('fill', 'yellow');
        redLabel.setAttribute('stroke', 'black');
        redLabel.setAttribute('stroke-width', '1');
        redLabel.setAttribute('font-size', '14px');
        redLabel.textContent = "Red Box Origin (0,0)";
        
        svgMarker.appendChild(redOrigin);
        svgMarker.appendChild(redLabel);
        
        // Append to the container
        if (containerRef.current) {
          containerRef.current.appendChild(svgMarker);
        }
      } else {
        // Update existing markers
        const redOrigin = document.getElementById('red-origin-marker');
        if (redOrigin) {
          redOrigin.setAttribute('cx', position.x);
          redOrigin.setAttribute('cy', position.y);
          
          const redLabel = document.getElementById('red-origin-marker-label');
          if (redLabel) {
            redLabel.setAttribute('x', position.x + 15);
            redLabel.setAttribute('y', position.y + 5);
          }
        }
      }
    };
    
    drawDebugMarkers();
    */
  }, [position, zoom]);

  // Update container dimensions when component mounts
  useEffect(() => {
    // This helps ensure the zoom hook can access container dimensions
    // for the initial calculations and when handling browser resize
    if (containerRef.current) {
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);
    }
  }, []);

  // Special effect to mark project as modified when spaces are changed
  useEffect(() => {
    if (boxes.length > 0 || shapes.length > 0 || entryPoints.length > 0) {
      setModified(true);
    }
  }, [boxes, shapes, entryPoints]);

  // Add keyboard event handlers for shift key tracking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.shiftKey && !shiftKeyPressed) {
        setShiftKeyPressed(true);
      }
    };
    
    const handleKeyUp = (e) => {
      if (!e.shiftKey && shiftKeyPressed) {
        setShiftKeyPressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [shiftKeyPressed]);

  // Function to show the keyboard shortcut notification
  const showShortcutNotification = (key, name) => {
    setShortcutNotification({ visible: true, key, name });
    
    // Hide notification after 1.5 seconds
    setTimeout(() => {
      setShortcutNotification(prev => ({ ...prev, visible: false }));
    }, 1500);
  };

  // Add keyboard shortcuts to the tool buttons
  const toggleAddBox = () => {
    if (activeMode === DRAWING_MODES.DRAWING_BOX) {
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      showShortcutNotification('V', 'Select Mode');
    } else {
      setActiveMode(DRAWING_MODES.DRAWING_BOX);
      // Cancel any active operations when switching modes
      cancelActiveOperation();
      showShortcutNotification('B', 'Box Tool');
    }
  };

  const toggleAddShape = () => {
    if (activeMode === DRAWING_MODES.DRAWING_SHAPE) {
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      showShortcutNotification('V', 'Select Mode');
    } else {
      setActiveMode(DRAWING_MODES.DRAWING_SHAPE);
      // Cancel any active operations when switching modes
      cancelActiveOperation();
      showShortcutNotification('F', 'Free Form Tool');
    }
  };

  const toggleAddEntry = () => {
    if (activeMode === DRAWING_MODES.ADDING_ENTRY) {
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      showShortcutNotification('V', 'Select Mode');
    } else {
      setActiveMode(DRAWING_MODES.ADDING_ENTRY);
      // Cancel any active operations when switching modes
      cancelActiveOperation();
      showShortcutNotification('E', 'Entry Tool');
    }
  };

  const toggleMeasureTool = () => {
    if (activeMode === DRAWING_MODES.MEASURING) {
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      showShortcutNotification('V', 'Select Mode');
    } else {
      setActiveMode(DRAWING_MODES.MEASURING);
      // Cancel any active operations when switching modes
      cancelActiveOperation();
      showShortcutNotification('M', 'Measure Tool');
    }
  };

  const toggleSetScale = () => {
    if (activeMode === DRAWING_MODES.SETTING_SCALE) {
      setActiveMode(DRAWING_MODES.SELECT_MODE);
      // Clear any in-progress scale measurement
      setMeasurePoints([]);
      setMeasureDistance(null);
      setInchesValue('');
      showShortcutNotification('V', 'Select Mode');
    } else {
      setActiveMode(DRAWING_MODES.SETTING_SCALE);
      // Start fresh with new measurement points
      setMeasurePoints([]);
      setMeasureDistance(null);
      setInchesValue('');
      // Cancel any other active operations
      cancelActiveOperation();
      showShortcutNotification('S', 'Scale Tool');
    }
  };
  
  // Handle space type selection with keyboard shortcuts
  const setSpaceType = (type) => {
    setCurrentSpaceType(type);
    
    // Show notification for space type change
    let typeName = 'Unknown';
    switch(type) {
      case SPACE_TYPES.DESK:
        typeName = 'Desk';
        showShortcutNotification('D', 'Desk Space');
        break;
      case SPACE_TYPES.ROOM:
        typeName = 'Room';
        showShortcutNotification('R', 'Meeting Room');
        break;
      case SPACE_TYPES.FLEX:
        typeName = 'Flex';
        showShortcutNotification('X', 'Flex Space');
        break;
      case SPACE_TYPES.PHONE:
        typeName = 'Phone';
        showShortcutNotification('P', 'Phone Booth');
        break;
      default:
        break;
    }
  };

  // Create refs for current hovered element and tooltip cleanup
  const currentHoveredElementRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);

  // Reusable function to create or update sensor tooltips
  const createOrUpdateSensorTooltip = (e, item, itemType, areaCalculator, sensorType, toggleFn) => {
    // Store the element reference
    currentHoveredElementRef.current = e.target;
    
    // Remove any existing tooltips first
    const existingTooltip = document.getElementById('sensor-tooltip');
    if (existingTooltip) existingTooltip.remove();
    
    // Create a tooltip with metadata
    const tooltip = document.createElement('div');
    tooltip.className = 'sensor-tooltip';
    
    // Calculate area and format it
    const areaSqFt = areaCalculator(item);
    const areaSqFtRounded = Math.ceil(areaSqFt);
    
    const oppositeType = sensorType === 'OA' ? 'Waffle' : 'OA';
    
    // Add rich information to tooltip
    tooltip.innerHTML = `
      <div><strong>${capitalizeFirstLetter(item.spaceCategory || 'Unknown')}</strong></div>
      <div>Type: ${capitalizeFirstLetter(item.spaceCategory || 'Unknown')}</div>
      <div>Area: ${areaSqFtRounded.toLocaleString()} sqft</div>
      <div>Sensor: ${sensorType}${item.count > 1 ? ` (x${item.count})` : ''}</div>
      <div class="tooltip-action">Click to change to ${oppositeType}</div>
    `;
    
    // Get the position of the sensor icon in the viewport
    const rect = e.target.getBoundingClientRect();
    const iconCenterX = rect.left + rect.width / 2;
    const iconTop = rect.top;
    
    tooltip.style.position = 'absolute';
    tooltip.style.top = `${iconTop - 8}px`; // Position just above the icon
    tooltip.style.left = `${iconCenterX}px`;
    tooltip.style.transform = 'translateX(-50%)'; // Center horizontally
    tooltip.style.backgroundColor = 'rgba(0,0,0,0.85)';
    tooltip.style.color = 'white';
    tooltip.style.padding = '8px 12px';
    tooltip.style.borderRadius = '4px';
    tooltip.style.fontSize = '12px';
    tooltip.style.zIndex = '1000';
    tooltip.style.opacity = '0.9';
    tooltip.style.transition = 'opacity 0.2s';
    tooltip.id = 'sensor-tooltip';
    tooltip.dataset.sensorType = sensorType;
    
    // If this is an update, don't append a new tooltip
    if (e.type !== 'mouseover') {
      return tooltip;
    }
    
    document.body.appendChild(tooltip);
    return tooltip;
  };

  // Reusable function to handle tooltip clicks
  const handleTooltipClick = (e, item, itemType, areaCalculator, toggleFn) => {
    e.stopPropagation();
    toggleFn(itemType, item.id);
    
    // Update tooltip text instead of removing it
    const tooltip = document.getElementById('sensor-tooltip');
    if (tooltip) {
      // Get the opposite sensor type from the current tooltip type
      const currentType = tooltip.dataset.sensorType;
      const newType = currentType === 'OA' ? 'WAFFLE' : 'OA';
      const displayType = newType === 'WAFFLE' ? 'Waffle' : 'OA';
      
      // Create updated tooltip
      createOrUpdateSensorTooltip(e, item, itemType, areaCalculator, displayType, toggleFn);
      
      // Reset auto-remove timer
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.style.opacity = '0';
          setTimeout(() => {
            if (tooltip.parentNode) {
              tooltip.remove();
            }
          }, 200);
        }
      }, 2000);
    }
  };

  // Handle keyboard shortcuts specific to the floorplan component
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Handle undo with 'z' key
      if (e.key === 'z' && !e.metaKey && !e.ctrlKey) {
        console.log('FloorplanViewer: Z key pressed - Calling handleUndo with scale', pixelsPerFoot);
        e.preventDefault();
        e.stopPropagation();
        handleUndo(pixelsPerFoot);
      }
    };
    
    // Add event listener with capture to ensure it runs first
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleUndo, pixelsPerFoot]);

  // Update the state to be true by default
  const [centroidVisibility, setCentroidVisibility] = useState(true);

  // Add a toggle function for centroid visibility
  const toggleCentroidVisibility = useCallback(() => {
    setCentroidVisibility(prevVisibility => !prevVisibility);
  }, []);

  // Add a toggle function for color by sensor type
  const toggleColorBySensorType = useCallback(() => {
    setColorBySensorType(prevState => !prevState);
  }, []);

  return (
    <div className="floorplan-viewer">
      {/* Show concurrent users indicator if we have a project ID */}
      {initialData?.id && <ConcurrentUsersIndicator projectId={initialData.id} />}
      
      {/* Shortcut notification */}
      <ShortcutNotification notification={shortcutNotification} />
      
      {/* Add top controls with Save button and Back to Projects link */}
      <div className="top-controls">
        <div className="button-group">
          <a href="/" className="top-button back-button">
            Back to Projects
          </a>
          
          {user && (
            <button 
              className="top-button save-button"
              onClick={saveProject}
              title="Save Project (Ctrl+S)"
            >
              Save
            </button>
          )}
          
          {/* Auto-save status indicators hidden for a cleaner UI */}
          {/* Hide auto-saving message 
          {user && initialData?.id && saveStatus === 'saving' && (
            <span className="save-status saving">Auto-saving</span>
          )}
          
          {user && initialData?.id && lastSaved && saveStatus !== 'saving' && (
            <span className="save-status">
              Last saved: {new Date(lastSaved).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              <span className="auto-save-note"> (Auto-saves every 30 sec)</span>
            </span>
          )}
          */}
          
          {user && !initialData?.id && (
            <div className="new-project-notice">
              This is a new project. Click Save to create it.
            </div>
          )}
        </div>
        
        {/* Integrated ToolPanel in top controls */}
        <ToolPanel 
          activeMode={activeMode}
          currentSpaceType={currentSpaceType}
          toggleMeasure={toggleMeasureTool}
          toggleSelectMode={toggleSelectMode}
          toggleSetScale={toggleSetScale}
          toggleDrawBox={toggleAddBox}
          toggleDrawShape={toggleAddShape}
          toggleAddEntry={toggleAddEntry}
          resetView={resetView}
          setCurrentSpaceType={setSpaceType}
          className="top-tool-panel"
        />
        
        {/* Add QuoteButton to the top controls */}
        <QuoteButton 
          sensorCounts={getSensorCountsForQuote()}
          projectName={initialData?.name || 'Untitled Project'}
          onShowSummary={onShowSummary}
          saveProject={saveProject}
        />
      </div>

      <div 
        className="viewer-container" 
        ref={containerRef} 
        onWheel={handleWheel}
      >
        {/* Single canvas container with both image and SVG in the same container */}
        <div 
          className="image-container" 
          style={transformStyle} 
          ref={imageRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* Image as the background */}
          <img 
            src={imageUrl} 
            alt="Floorplan" 
            className="floorplan-image" 
            draggable={false}
            onLoad={handleImageLoad}
          />
          
          {/* SVG overlay drawn directly in the same container */}
          <svg 
            className="measurement-overlay" 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none'
            }}
          >
            {/* All SVG content */}
            {/* Reference line and measurement points */}
            <ScaleTool 
              renderType="svg"
              isSettingScale={activeMode === DRAWING_MODES.SETTING_SCALE}
              measurePoints={measurePoints}
              inchesValue={inchesValue}
              referenceLine={referenceLine}
              transformPoint={null}
              handleInchesChange={handleInchesChange}
              applyScale={applyScale}
              FEET_TO_METERS={FEET_TO_METERS}
              METERS_TO_FEET={METERS_TO_FEET}
              pixelsPerFoot={pixelsPerFoot}
              measureDistance={measureDistance}
              zoom={zoom}
              currentMousePos={currentMousePos}
              shiftPressed={shiftKeyPressed}
            />
            
            <MeasurementOverlay 
              drawing={{
                drawingMode: activeMode,
                boxStart,
                boxEnd,
                shapePoints,
                isClosingShape,
                measurePoints,
                measureDistance
              }}
              boxes={boxes}
              shapes={shapes}
              entryPoints={entryPoints}
              selectedSpace={selectedSpace}
              scale={zoom}
              position={position}
              pixelsPerFoot={pixelsPerFoot}
              referenceLine={referenceLine}
              spaceTypeVisibility={spaceTypeVisibility}
              shiftKeyPressed={shiftKeyPressed}
              currentMousePos={currentMousePos}
            />
            
            {/* Draw current box preview */}
            {boxStart && boxEnd && (
              <rect
                x={getBoxCoordinates(boxStart, boxEnd)?.x}
                y={getBoxCoordinates(boxStart, boxEnd)?.y}
                width={getBoxCoordinates(boxStart, boxEnd)?.width}
                height={getBoxCoordinates(boxStart, boxEnd)?.height}
                fill="rgba(0, 100, 255, 0.2)"
                stroke="blue"
                strokeWidth={2 / zoom} // Scale stroke width inversely with zoom
              />
            )}
            
            {/* Draw current shape preview */}
            {activeMode === DRAWING_MODES.DRAWING_SHAPE && (
              <ShapeDrawingTool 
                shapePoints={shapePoints} 
                isClosingShape={isClosingShape} 
                transformPoint={null}
                zoom={zoom}
                shiftPressed={shiftKeyPressed}
                currentMousePos={currentMousePos}
              />
            )}
            
            {/* Draw boxes */}
            {boxes.map(box => {
              // Skip rendering if box is not visible and type is not visible
              // Also skip if the space category is not visible
              if (box.visible === false || 
                  typeVisibility[box.type] === false || 
                  !isSpaceTypeVisible(box)) return null;
              
              const isSelected = selectedSpace && selectedSpace.type === 'box' && selectedSpace.id === box.id;
              const isMultiSelected = selectedSpaces.some(space => space.type === 'box' && space.id === box.id);
              
              // Get coordinates for the box
              const { x, y, width, height, centerX, centerY } = getBoxCoordinates(box.start, box.end);
              
              // Get color based on sensor type or space category
              let spaceColor;
              if (colorBySensorType) {
                spaceColor = SENSOR_COLORS[box.type] || '#888888'; // Use sensor color or gray if undefined
              } else {
                spaceColor = box.spaceCategory && SPACE_TYPE_COLORS[box.spaceCategory] 
                  ? SPACE_TYPE_COLORS[box.spaceCategory] 
                  : SPACE_TYPE_COLORS[SPACE_TYPES.DESK]; // Default to desk color
              }
              
              return (
                <g key={`box-${box.id}`}>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={spaceColor}
                    fillOpacity="0.35"
                    stroke={isSelected || isMultiSelected ? "yellow" : getDarkerColor(spaceColor)}
                    strokeWidth={isSelected || isMultiSelected ? 3 / zoom : 2 / zoom}
                    onClick={(e) => {
                      // Only allow selection when in SELECT_MODE
                      if (activeMode !== DRAWING_MODES.SELECT_MODE) return;
                      
                      if (e.metaKey || e.ctrlKey) { // Support both Mac and Windows
                        handleCommandClick(e, 'box', box.id);
                      } else {
                        e.stopPropagation();
                        setSelectedSpace({ type: 'box', id: box.id });
                        // If user has selected a single space, allow for direct deletion when pressing Delete key
                        setSelectedSpaces([]);
                        setIsMultiSelecting(false);
                      }
                    }}
                  />
                  {/* Display sensor type indicators instead of IDs */}
                  {box.type === SENSOR_TYPES.OA ? (
                    // For OA sensors, draw a square with border matching space category color
                    <g>
                      <rect
                        x={centerX - 5.2 / zoom}
                        y={centerY - 5.2 / zoom}
                        width={10.4 / zoom}
                        height={10.4 / zoom}
                        fill="#FFFFFF"
                        stroke={spaceColor}
                        strokeWidth={1 / zoom}
                        className="sensor-icon"
                        style={{
                          pointerEvents: centroidVisibility ? 'all' : 'none', 
                          cursor: 'pointer', 
                          opacity: centroidVisibility ? 1 : 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseOver={(e) => {
                          createOrUpdateSensorTooltip(e, box, 'box', calculateBoxArea, 'OA', toggleSpaceType);
                        }}
                        onMouseOut={(e) => {
                          // Only remove tooltip if we're actually leaving the element
                          // This prevents tooltip from disappearing when moving inside the element
                          if (e.relatedTarget !== currentHoveredElementRef.current) {
                            const tooltip = document.getElementById('sensor-tooltip');
                            if (tooltip) tooltip.remove();
                            currentHoveredElementRef.current = null;
                          }
                        }}
                        onClick={(e) => {
                          handleTooltipClick(e, box, 'box', calculateBoxArea, toggleSpaceType);
                        }}
                      />
                    </g>
                  ) : box.type === SENSOR_TYPES.WAFFLE ? (
                    // For Waffle sensors, draw a circle with border matching space category color
                    <g>
                      <circle
                        cx={centerX}
                        cy={centerY}
                        r={5.85 / zoom}
                        fill="#FFFFFF"
                        stroke={spaceColor}
                        strokeWidth={1 / zoom}
                        className="sensor-icon"
                        style={{
                          pointerEvents: centroidVisibility ? 'all' : 'none', 
                          cursor: 'pointer', 
                          opacity: centroidVisibility ? 1 : 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseOver={(e) => {
                          createOrUpdateSensorTooltip(e, box, 'box', calculateBoxArea, 'Waffle', toggleSpaceType);
                        }}
                        onMouseOut={(e) => {
                          // Only remove tooltip if we're actually leaving the element
                          // This prevents tooltip from disappearing when moving inside the element
                          if (e.relatedTarget !== currentHoveredElementRef.current) {
                            const tooltip = document.getElementById('sensor-tooltip');
                            if (tooltip) tooltip.remove();
                            currentHoveredElementRef.current = null;
                          }
                        }}
                        onClick={(e) => {
                          handleTooltipClick(e, box, 'box', calculateBoxArea, toggleSpaceType);
                        }}
                      />
                    </g>
                  ) : box.type === SENSOR_TYPES.ENTRY ? (
                    // For Entry types, only show square without ID
                    <rect
                      x={centerX - 9 / zoom}
                      y={centerY - 9 / zoom}
                      width={18 / zoom}
                      height={18 / zoom}
                      fill="#FFFFFF"
                      stroke={SPACE_TYPE_COLORS[SPACE_TYPES.ENTRY]}
                      strokeWidth={1 / zoom}
                      pointerEvents="none"
                      style={{
                        opacity: centroidVisibility ? 1 : 0,
                        transition: 'opacity 0.2s'
                      }}
                    />
                  ) : (
                    // For other types, keep showing the ID
                    <text
                      x={centerX}
                      y={centerY}
                      fill="white"
                      textAnchor="middle"
                      fontSize={16 / zoom}
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      {box.id}
                    </text>
                  )}
                </g>
              );
            })}
            
            {/* Draw shapes */}
            {shapes.map(shape => {
              // Skip rendering if shape is not visible and type is not visible
              // Also skip if the space category is not visible
              if (shape.visible === false || 
                  typeVisibility[shape.type] === false || 
                  !isSpaceTypeVisible(shape)) return null;
              
              const isSelected = selectedSpace && selectedSpace.type === 'shape' && selectedSpace.id === shape.id;
              const isMultiSelected = selectedSpaces.some(space => space.type === 'shape' && space.id === shape.id);
              
              // Create the SVG path string - no transformations needed now
              let pathData = '';
              shape.points.forEach((point, index) => {
                if (index === 0) {
                  pathData += `M ${point.x} ${point.y}`;
                } else {
                  pathData += ` L ${point.x} ${point.y}`;
                }
              });
              // Close the path
              pathData += ' Z';
              
              // Calculate centroid for label placement
              const centroid = shape.points.reduce(
                (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
                { x: 0, y: 0 }
              );
              centroid.x /= shape.points.length;
              centroid.y /= shape.points.length;
              
              // Get color based on sensor type or space category
              let spaceColor;
              if (colorBySensorType) {
                spaceColor = SENSOR_COLORS[shape.type] || '#888888'; // Use sensor color or gray if undefined
              } else {
                spaceColor = shape.spaceCategory && SPACE_TYPE_COLORS[shape.spaceCategory] 
                  ? SPACE_TYPE_COLORS[shape.spaceCategory] 
                  : SPACE_TYPE_COLORS[SPACE_TYPES.DESK]; // Default to desk color
              }
              
              return (
                <g key={`shape-${shape.id}`}>
                  <path
                    d={pathData}
                    fill={spaceColor}
                    fillOpacity="0.35"
                    stroke={isSelected || isMultiSelected ? "yellow" : getDarkerColor(spaceColor)}
                    strokeWidth={isSelected || isMultiSelected ? 3 / zoom : 2 / zoom}
                    onClick={(e) => {
                      // Only allow selection when in SELECT_MODE
                      if (activeMode !== DRAWING_MODES.SELECT_MODE) return;
                      
                      if (e.metaKey || e.ctrlKey) { // Support both Mac and Windows
                        handleCommandClick(e, 'shape', shape.id);
                      } else {
                        e.stopPropagation();
                        setSelectedSpace({ type: 'shape', id: shape.id });
                        setSelectedSpaces([]);
                        setIsMultiSelecting(false);
                      }
                    }}
                  />
                  {/* Display sensor type indicators instead of IDs */}
                  {shape.type === SENSOR_TYPES.OA ? (
                    // For OA sensors, draw a square with border matching space category color
                    <g>
                      <rect
                        x={centroid.x - 5.2 / zoom}
                        y={centroid.y - 5.2 / zoom}
                        width={10.4 / zoom}
                        height={10.4 / zoom}
                        fill="#FFFFFF"
                        stroke={spaceColor}
                        strokeWidth={1 / zoom}
                        className="sensor-icon"
                        style={{
                          pointerEvents: centroidVisibility ? 'all' : 'none', 
                          cursor: 'pointer', 
                          opacity: centroidVisibility ? 1 : 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseOver={(e) => {
                          createOrUpdateSensorTooltip(e, shape, 'shape', calculateShapeArea, 'OA', toggleSpaceType);
                        }}
                        onMouseOut={(e) => {
                          // Only remove tooltip if we're actually leaving the element
                          // This prevents tooltip from disappearing when moving inside the element
                          if (e.relatedTarget !== currentHoveredElementRef.current) {
                            const tooltip = document.getElementById('sensor-tooltip');
                            if (tooltip) tooltip.remove();
                            currentHoveredElementRef.current = null;
                          }
                        }}
                        onClick={(e) => {
                          handleTooltipClick(e, shape, 'shape', calculateShapeArea, toggleSpaceType);
                        }}
                      />
                    </g>
                  ) : shape.type === SENSOR_TYPES.WAFFLE ? (
                    // For Waffle sensors, draw a circle with border matching space category color
                    <g>
                      <circle
                        cx={centroid.x}
                        cy={centroid.y}
                        r={5.85 / zoom}
                        fill="#FFFFFF"
                        stroke={spaceColor}
                        strokeWidth={1 / zoom}
                        className="sensor-icon"
                        style={{
                          pointerEvents: centroidVisibility ? 'all' : 'none', 
                          cursor: 'pointer', 
                          opacity: centroidVisibility ? 1 : 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseOver={(e) => {
                          createOrUpdateSensorTooltip(e, shape, 'shape', calculateShapeArea, 'Waffle', toggleSpaceType);
                        }}
                        onMouseOut={(e) => {
                          // Only remove tooltip if we're actually leaving the element
                          // This prevents tooltip from disappearing when moving inside the element
                          if (e.relatedTarget !== currentHoveredElementRef.current) {
                            const tooltip = document.getElementById('sensor-tooltip');
                            if (tooltip) tooltip.remove();
                            currentHoveredElementRef.current = null;
                          }
                        }}
                        onClick={(e) => {
                          handleTooltipClick(e, shape, 'shape', calculateShapeArea, toggleSpaceType);
                        }}
                      />
                    </g>
                  ) : (
                    // For other types, show ID (but there shouldn't be any other types)
                    <text
                      x={centroid.x}
                      y={centroid.y}
                      fill="white"
                      textAnchor="middle"
                      fontSize={16 / zoom}
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      {shape.id}
                    </text>
                  )}
                </g>
              );
            })}
            
            {/* Draw entry points */}
            {entryPoints.map(entry => {
              // Skip rendering if entry is not visible 
              // Also skip if the space category is not visible
              if (entry.visible === false || 
                  !isSpaceTypeVisible({ spaceCategory: SPACE_TYPES.ENTRY })) return null;
              
              const isSelected = selectedSpace && selectedSpace.type === 'entry' && selectedSpace.id === entry.id;
              const isMultiSelected = selectedSpaces.some(space => space.type === 'entry' && space.id === entry.id);
              
              // Size for square entry points - half the previous size
              const size = isSelected || isMultiSelected ? 11 / zoom : 10 / zoom;
              
              return (
                <g key={`entry-${entry.id}`}>
                  <rect
                    x={entry.position.x - size/2}
                    y={entry.position.y - size/2}
                    width={size}
                    height={size}
                    fill={SENSOR_COLORS[SENSOR_TYPES.ENTRY]} // Solid purple fill
                    stroke={isSelected || isMultiSelected ? "yellow" : SPACE_TYPE_COLORS[SPACE_TYPES.ENTRY]}
                    strokeWidth={isSelected || isMultiSelected ? 3 / zoom : 2 / zoom}
                    onClick={(e) => {
                      // Only allow selection when in SELECT_MODE
                      if (activeMode !== DRAWING_MODES.SELECT_MODE) return;
                      
                      if (e.metaKey || e.ctrlKey) { // Support both Mac and Windows
                        handleCommandClick(e, 'entry', entry.id);
                      } else {
                        e.stopPropagation();
                        setSelectedSpace({ type: 'entry', id: entry.id });
                        // If user has selected a single space, allow for direct deletion when pressing Delete key
                        setSelectedSpaces([]);
                        setIsMultiSelecting(false);
                      }
                    }}
                  />
                  {/* Entry points no longer display ID numbers */}
                </g>
              );
            })}
          </svg>
        </div>
        
        {/* Render the scale warning at the bottom of the screen if scale is not set */}
        {!referenceLine && (
          <div className="scale-warning">
            <p>❗ Please set the scale using the 📐 tool for accurate square footage calculations.</p>
          </div>
        )}
        
        {/* Box area display */}
        {boxArea !== null && (
          <div className="area-display">
            Area: {formatArea(boxArea)}
          </div>
        )}
        
        {/* Selection instructions - REMOVED as requested */}
        
        {/* Box drawing instructions */}
        {activeMode === DRAWING_MODES.DRAWING_BOX && boxStart && !boxEnd && (
          <div className="measure-instructions">
            Click to set opposite corner
          </div>
        )}
        
        
        {/* Shape drawing instructions */}
        {activeMode === DRAWING_MODES.DRAWING_SHAPE && (
          <div className="measure-instructions">
            {shapePoints.length === 0 
              ? "Click to place first point" 
              : shapePoints.length < 3 
                ? "Click to add more points (min 3 needed)" 
                : "Click to add more points or click first point to complete shape"}
          </div>
        )}
        
        {/* Scale setting panel */}
        <ScaleTool
          renderType="panel"
          isSettingScale={activeMode === DRAWING_MODES.SETTING_SCALE}
          measurePoints={measurePoints}
          inchesValue={inchesValue}
          referenceLine={referenceLine}
          transformPoint={null}
          handleInchesChange={handleInchesChange}
          applyScale={applyScale}
          FEET_TO_METERS={FEET_TO_METERS}
          METERS_TO_FEET={METERS_TO_FEET}
          pixelsPerFoot={pixelsPerFoot}
          measureDistance={measureDistance}
          zoom={zoom}
          currentMousePos={currentMousePos}
          shiftPressed={shiftKeyPressed}
        />
        
        {/* Add Space Legend */}
        <SpaceLegend 
          onToggleSpaceType={toggleSpaceTypeVisibility}
          spaceTypeVisibility={spaceTypeVisibility}
          onToggleSensorType={toggleTypeVisibility}
          sensorTypeVisibility={typeVisibility}
          onToggleCentroid={toggleCentroidVisibility}
          centroidVisibility={centroidVisibility}
          onToggleColorBySensorType={toggleColorBySensorType}
          colorBySensorType={colorBySensorType}
        />
      </div>
      
      {/* Space list table */}
      <div className="space-list">
        <div className="space-list-header">
          <h3>Spaces</h3>
          <div className="space-list-actions">
            {(boxes.length > 0 || shapes.length > 0 || entryPoints.length > 0) && (
              <>
                <DeleteSelectedSpacesButton 
                  selectedSpaces={selectedSpaces}
                  onDelete={deleteSelectedSpaces}
                />
                <button 
                  className="action-button clear-button" 
                  onClick={clearAllSpaces}
                  title="Delete all spaces"
                >
                  Delete All
                </button>
                {/* Hide export data button for now */}
                {/* <button 
                  className="action-button export-button" 
                  onClick={exportData}
                  title="Export data as JSON"
                >
                  Export Data
                </button> */}
              </>
            )}
          </div>
        </div>
        
        <SpaceTable
          boxes={boxes}
          shapes={shapes}
          entryPoints={entryPoints}
          selectedSpace={selectedSpace}
          selectedSpaces={selectedSpaces}
          isMultiSelecting={isMultiSelecting}
          activeMode={activeMode}
          setSelectedSpace={setSelectedSpace}
          setSelectedSpaces={setSelectedSpaces}
          setIsMultiSelecting={setIsMultiSelecting}
          toggleSpaceType={toggleSpaceType}
          updateSpaceCount={updateSpaceCount}
          toggleSpaceVisibility={toggleSpaceVisibility}
          deleteSpace={deleteSpace}
          handleCommandClick={handleCommandClick}
          calculateTotalArea={calculateTotalArea}
          calculateSensorCounts={calculateSensorCounts}
          typeVisibility={typeVisibility}
          toggleTypeVisibility={toggleTypeVisibility}
          toggleSelectedSpacesVisibility={toggleSelectedSpacesVisibility}
          updateSpaceAttribute={updateSpaceAttribute}
          pixelsPerFoot={pixelsPerFoot}
        />
      </div>
    </div>
  );
};

// ShortcutNotification component - Pass the state as props
const ShortcutNotification = ({ notification }) => {
  return (
    <div className={`shortcut-notification ${notification.visible ? 'visible' : ''}`}>
      <span className="shortcut-key">{notification.key}</span>
      <span className="shortcut-name">{notification.name}</span>
    </div>
  );
};

export default FloorplanViewer; 